/**
 * PixelCard 컴포넌트
 * 레트로 게임 스타일의 픽셀 카드
 * - 게임보이 LCD 스크린 스타일
 * - 다크 패널 스타일
 * - 도트 테두리 및 그림자
 */

import React, { HTMLAttributes } from 'react';

/**
 * PixelCard Props
 */
interface PixelCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * 카드 제목 (선택사항)
   */
  title?: string;

  /**
   * 카드 스타일 변형
   * - screen: 게임보이 LCD 스크린 스타일 (밝은 그린)
   * - panel: 다크 패널 스타일 (회색/검정)
   * - outline: 테두리만 있는 스타일
   */
  variant?: 'screen' | 'panel' | 'outline';

  /**
   * 패딩 크기
   */
  padding?: 'small' | 'medium' | 'large';
}

/**
 * PixelCard 컴포넌트
 * 레트로 게임 스타일의 도트 카드
 */
export const PixelCard: React.FC<PixelCardProps> = ({
  title,
  variant = 'panel',
  padding = 'medium',
  children,
  className = '',
  ...props
}) => {
  // 패딩 크기
  const paddingClasses = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
  };

  // 변형별 스타일
  const variantStyles = {
    screen: {
      className: 'bg-[#9bbc0f]',
      style: {
        border: '4px solid #1f2937',
        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.3)',
      },
      innerClassName: 'bg-[#8bac0f]',
      innerStyle: {
        border: '2px solid #0f380f',
      },
      textColor: 'text-[#0f380f]',
    },
    panel: {
      className: 'bg-gray-800',
      style: {
        border: '4px solid #4b5563',
        boxShadow: '0 4px 0 #374151, 0 6px 12px rgba(0,0,0,0.5)',
      },
      innerClassName: 'bg-gray-900',
      innerStyle: {
        border: '2px solid #1f2937',
      },
      textColor: 'text-white',
    },
    outline: {
      className: 'bg-transparent',
      style: {
        border: '3px solid #6b7280',
      },
      innerClassName: '',
      innerStyle: {},
      textColor: 'text-white',
    },
  };

  const variantStyle = variantStyles[variant];

  return (
    <div
      className={`${variantStyle.className} ${className}`}
      style={variantStyle.style}
      {...props}
    >
      <div
        className={`${variantStyle.innerClassName} ${paddingClasses[padding]}`}
        style={variantStyle.innerStyle}
      >
        {/* 제목 (선택사항) */}
        {title && (
          <div className="mb-4">
            <h3
              className={`font-pixel text-lg ${variantStyle.textColor}`}
              style={{
                textShadow: variant === 'screen' ? 'none' : '1px 1px 0 #000',
              }}
            >
              {title}
            </h3>
            <div
              className="mt-2"
              style={{
                height: '2px',
                background:
                  variant === 'screen'
                    ? '#0f380f'
                    : 'repeating-linear-gradient(90deg, #4b5563 0px, #4b5563 4px, transparent 4px, transparent 8px)',
              }}
            />
          </div>
        )}

        {/* 콘텐츠 */}
        <div className={variantStyle.textColor}>{children}</div>
      </div>
    </div>
  );
};
